
import { useState } from 'react';
import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import { UserProfileForm } from '@/components/user/UserProfileForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { NavLink } from '@/components/NavLink';
import { Settings as SettingsIcon, Bell, Shield, Smartphone, Webhook } from 'lucide-react';

export default function Settings() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [twoFactorAuth, setTwoFactorAuth] = useState(false);
  const [phone, setPhone] = useState('');

  const handleSaveNotifications = () => {
    toast.success("Notification preferences saved");
  };

  const handleSaveSecurity = () => {
    toast.success("Security settings saved");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Settings</h2>
            <p className="text-muted-foreground">
              Manage your account settings and preferences.
            </p>
          </div>
          <SettingsIcon className="h-6 w-6 text-muted-foreground" />
        </div>
        <Separator />
        
        <div className="flex gap-8">
          <div className="w-1/5">
            <nav className="flex flex-col space-y-2">
              <NavLink to="/settings" className="p-2 rounded-md hover:bg-accent">General</NavLink>
              <NavLink to="/settings/developer" className="p-2 rounded-md hover:bg-accent">Developer</NavLink>
            </nav>
          </div>
          
          <div className="flex-1">
            <Tabs defaultValue="general" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full max-w-md grid-cols-3">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>
              
              <TabsContent value="general" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>
                      Update your personal details and how others see you on the platform.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <UserProfileForm user={user} />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="notifications" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Notification Preferences</CardTitle>
                        <CardDescription>
                          Configure how you receive notifications and updates.
                        </CardDescription>
                      </div>
                      <Bell className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between space-x-2">
                      <Label htmlFor="email-notifications" className="flex flex-col space-y-1">
                        <span>Email Notifications</span>
                        <span className="font-normal text-xs text-muted-foreground">Receive notifications via email</span>
                      </Label>
                      <Switch 
                        id="email-notifications" 
                        checked={emailNotifications} 
                        onCheckedChange={setEmailNotifications} 
                      />
                    </div>
                    <div className="flex items-center justify-between space-x-2">
                      <Label htmlFor="push-notifications" className="flex flex-col space-y-1">
                        <span>Push Notifications</span>
                        <span className="font-normal text-xs text-muted-foreground">Receive notifications on your device</span>
                      </Label>
                      <Switch 
                        id="push-notifications" 
                        checked={pushNotifications} 
                        onCheckedChange={setPushNotifications} 
                      />
                    </div>
                    
                    <Button onClick={handleSaveNotifications} className="mt-4">Save Notification Settings</Button>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="security" className="mt-6">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Security Settings</CardTitle>
                        <CardDescription>
                          Manage your account security and authentication.
                        </CardDescription>
                      </div>
                      <Shield className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between space-x-2">
                      <Label htmlFor="two-factor-auth" className="flex flex-col space-y-1">
                        <span>Two-Factor Authentication</span>
                        <span className="font-normal text-xs text-muted-foreground">Add an extra layer of security to your account</span>
                      </Label>
                      <Switch 
                        id="two-factor-auth" 
                        checked={twoFactorAuth} 
                        onCheckedChange={setTwoFactorAuth} 
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone-number" className="flex flex-col space-y-1">
                        <span>Phone Number</span>
                        <span className="font-normal text-xs text-muted-foreground">For SMS verification (optional)</span>
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Input 
                          id="phone-number"
                          type="tel"
                          placeholder="+1 (555) 000-0000"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          className="flex-1"
                        />
                        <Smartphone className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                    
                    <Button onClick={handleSaveSecurity} className="mt-4">Save Security Settings</Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
